import { blurIfEscape } from '@/common';
import { pubsub } from '@/pubsub';
import { tempStorage } from '@/storage';
import { type VNode, type VNodeData, snabH, thunk, enter, alert, onInsert, input, li, span } from '@/view';
import { userLink } from '@/view/userLink';

import { licon } from '../licon';
import * as enhance from '../richText';
import type { ChatCtrl } from './chatCtrl';
import type { Line } from './interfaces';
import { lineAction as modLineAction, flagReport } from './moderation';
import { presetView } from './preset';
import * as spam from './spam';

const whisperRegex = /^\/[wW](?:hisper)?\s/;
const scrollState = { pinToBottom: true, lastScrollTop: 0 };
let resizeObserver: ResizeObserver | null = null;

const scrollToBottom = (el: HTMLElement, smooth: boolean): void => {
  if (document.hidden || !smooth) el.scrollTop = el.scrollHeight;
  else if (el.scrollTop + el.clientHeight < el.scrollHeight)
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  scrollState.lastScrollTop = el.scrollTop;
};

export default function (ctrl: ChatCtrl): Array<VNode | undefined> {
  if (!ctrl.chatEnabled()) return [];
  const hasMod = !!ctrl.moderation;
  const vnodes = [
    snabH(
      `ol.mchat__messages.chat-v-${ctrl.vm.domVersion}`,
      {
        class: { 'as-mod': hasMod },
        attrs: { role: 'log', 'aria-live': 'polite', 'aria-atomic': 'false' },
        hook: {
          ...onInsert(el => {
            const $el = $(el).on('click', 'a.jump', (e: Event) => {
              const ply = (e.target as HTMLElement).getAttribute('data-ply');
              if (ply) pubsub.emit('jump', ply);
            });
            $el.on('click', '.reply', (e: Event) => {
              const el = e.target as HTMLElement;
              const username = el.parentElement
                ?.querySelector<HTMLLinkElement>('.user-link')
                ?.getAttribute('href')
                ?.slice(3);
              const input = el.closest('.mchat')?.querySelector<HTMLInputElement>('input.mchat__say');
              if (username && input) prependChatInput(input, `@${username} `);
            });
            if (hasMod)
              $el.on('click', '.mod', (e: Event) =>
                ctrl.moderation?.open((e.target as HTMLElement).parentNode as HTMLElement),
              );
            else $el.on('click', '.flag', (e: Event) => flagReport(ctrl, e.target as HTMLElement));

            el.addEventListener('scroll', () => {
              if (el.scrollTop < scrollState.lastScrollTop) scrollState.pinToBottom = false;
              else if (el.scrollTop + el.clientHeight > el.scrollHeight - 10) scrollState.pinToBottom = true;
              scrollState.lastScrollTop = el.scrollTop;
            });

            resizeObserver?.disconnect();
            resizeObserver = new ResizeObserver(() => {
              if (scrollState.pinToBottom) scrollToBottom(el, false);
            });
            resizeObserver.observe(el);
            requestAnimationFrame(() => scrollToBottom(el, false));
          }),
          postpatch: (_, vnode) => {
            if (scrollState.pinToBottom) scrollToBottom(vnode.elm as HTMLElement, true);
          },
          destroy(_) {
            resizeObserver?.disconnect();
            resizeObserver = null;
          },
        },
      },
      selectLines(ctrl).map(line => renderLine(ctrl, line)),
    ),
    renderInput(ctrl),
  ];
  const presets = presetView(ctrl.preset);
  if (presets) vnodes.push(presets);
  return vnodes;
}

function renderInput(ctrl: ChatCtrl): VNode | undefined {
  if (!ctrl.vm.writeable) return undefined;
  if ((ctrl.data.loginRequired && !ctrl.data.userId) || ctrl.data.restricted)
    return input('text')('.mchat__say', {
      placeholder: i18n.site.loginToChat,
      disabled: true,
    });
  let placeholder: string;
  if (ctrl.vm.timeout) placeholder = i18n.site.youHaveBeenTimedOut;
  else if (ctrl.opts.blind) placeholder = 'Chat';
  else placeholder = i18n.site.talkInChat;
  return input('text')('.mchat__say', {
    placeholder,
    autocomplete: 'off',
    enterkeyhint: 'send',
    maxlength: 140,
    disabled: ctrl.vm.timeout || !ctrl.vm.writeable,
    'aria-label': 'Chat input',
    hook: onInsert<HTMLInputElement>(el => setupHooks(ctrl, el)),
  });
}

function prependChatInput(chatInput: HTMLInputElement, prefix: string): void {
  if (!chatInput.value.includes(prefix)) chatInput.value = prefix + chatInput.value;
  chatInput.focus();
  chatInput.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
}

let mouchListener: EventListener;

const setupHooks = (ctrl: ChatCtrl, chatEl: HTMLInputElement) => {
  const inner = tempStorage.make(`chat.input`);
  const storage = {
    get: () => {
      const v = inner.get();
      if (v) {
        try {
          const parsed = JSON.parse(v);
          if (parsed[0] === (ctrl.data.opponentId || '')) {
            return parsed[1] as string;
          }
        } catch {
          console.log(`Could not parse "chat.input" value ${v}`);
        }
      }
      return undefined;
    },
    set: (txt: string) => {
      inner.set(JSON.stringify([ctrl.data.opponentId || '', txt]));
    },
    inner,
  };
  const previousText = storage.get();
  if (previousText) {
    chatEl.value = previousText;
    chatEl.focus();
    if (!ctrl.opts.public && previousText.match(whisperRegex)) chatEl.classList.add('whisper');
  } else if (ctrl.vm.autofocus) chatEl.focus();

  chatEl.addEventListener('keydown', e => {
    blurIfEscape(e);
    enter(target => {
      setTimeout(() => {
        const el = target as HTMLInputElement;
        const txt = el.value;
        const pub = ctrl.opts.public;

        if (txt === '')
          $('.input-move input').each(function (this: HTMLInputElement) {
            this.focus();
          });
        else {
          if (!ctrl.opts.kobold) spam.selfReport(txt);
          if (pub && spam.hasTeamUrl(txt)) alert("Please don't advertise teams in the chat.");
          else {
            scrollState.pinToBottom = true;
            ctrl.post(txt);
          }
          el.value = '';
          storage.inner.remove();
          if (!pub) el.classList.remove('whisper');
        }
      });
    })(e);
  });

  chatEl.addEventListener('input', (e: KeyboardEvent) =>
    setTimeout(() => {
      const el = e.target as HTMLInputElement;
      const txt = el.value;

      el.removeAttribute('placeholder');
      if (!ctrl.opts.public) el.classList.toggle('whisper', whisperRegex.test(txt));
      storage.set(txt);
    }),
  );

  site.mousetrap.bind(
    'c',
    () => document.querySelector<HTMLInputElement>('input.mchat__say')?.focus(),
    undefined,
    false,
  );

  // Ensure clicks remove chat focus.
  // See https://github.com/lichess-org/lila/pull/5323

  const mouchEvents = ['touchstart', 'mousedown'];

  if (mouchListener)
    mouchEvents.forEach(event => document.body.removeEventListener(event, mouchListener, { capture: true }));

  mouchListener = (e: MouseEvent) => {
    if (!e.shiftKey && e.buttons !== 2 && e.button !== 2 && e.target !== chatEl) chatEl.blur();
  };

  chatEl.onfocus = () =>
    mouchEvents.forEach(event =>
      document.body.addEventListener(event, mouchListener, { passive: true, capture: true }),
    );

  chatEl.onblur = () =>
    mouchEvents.forEach(event => document.body.removeEventListener(event, mouchListener, { capture: true }));
};

const sameLines = (l1: Line, l2: Line) => l1.d && l2.d && l1.u === l2.u;

function selectLines(ctrl: ChatCtrl): Array<Line> {
  const ls: Array<Line> = [];
  let prev: Line | undefined;
  ctrl.data.lines.forEach(line => {
    if (
      !line.d &&
      (!prev || !sameLines(prev, line)) &&
      (!line.r || (line.u || '').toLowerCase() === ctrl.data.userId) &&
      !spam.skip(line.t)
    )
      ls.push(line);
    prev = line;
  });
  return ls;
}

const updateText = (opts?: enhance.EnhanceOpts) => (oldVnode: VNode, vnode: VNode) => {
  if ((vnode.data as VNodeData).lichessChat !== (oldVnode.data as VNodeData).lichessChat)
    (vnode.elm as HTMLElement).innerHTML = enhance.enhance((vnode.data as VNodeData).lichessChat, opts);
};

const profileLinkRegex = /(https:\/\/)?lichess\.org\/@\/([a-zA-Z0-9_-]+)/g;

const processProfileLink = (text: string) => text.replace(profileLinkRegex, '@$2');

function renderText(t: string, opts?: enhance.EnhanceOpts) {
  const processedText = processProfileLink(t);
  if (enhance.isMoreThanText(processedText)) {
    const hook = updateText(opts);
    return snabH('t', { lichessChat: processedText, hook: { create: hook, update: hook } });
  }
  return snabH('t', processedText);
}

const userThunk = (name: string, title?: string, patronColor?: PatronColor, flair?: Flair) =>
  userLink({ name, title, patronColor, line: !!patronColor, flair, online: !!patronColor });

const actionIcons = (ctrl: ChatCtrl, line: Line): Array<VNode | null> => {
  if (!ctrl.data.userId || !line.u || ctrl.data.userId === line.u) return [];
  const icons = [];
  if (ctrl.canPostArbitraryText() && !ctrl.data.resourceId.startsWith('game'))
    icons.push(
      snabH('action.reply', {
        attrs: { 'data-icon': licon.Back, title: 'Reply' },
      }),
    );
  icons.push(
    ctrl.moderation
      ? modLineAction()
      : snabH('action.flag', {
          attrs: { 'data-icon': licon.CautionTriangle, title: 'Report', 'data-text': line.t },
        }),
  );
  return icons;
};

function renderLine(ctrl: ChatCtrl, line: Line): VNode {
  const textNode = renderText(line.t, ctrl.opts.enhance);

  if (line.u === 'lichess') return li('.system', textNode);

  if (line.c) return li([span('.color', '[' + line.c + ']'), textNode]);

  const userNode = thunk('a', line.u, userThunk, [line.u, line.title, line.pc, line.f]);
  const userId = line.u?.toLowerCase();

  const myUserId = ctrl.data.userId;
  const mentioned =
    !!myUserId &&
    !!line.t
      .match(enhance.userPattern)
      ?.find(mention => mention.trim().toLowerCase() === `@${ctrl.data.userId}`);

  return li(
    {
      class: {
        me: userId === myUserId,
        host: !!(userId && ctrl.data.hostIds?.includes(userId)),
        mentioned,
      },
    },
    [...actionIcons(ctrl, line), userNode, ' ', textNode],
  );
}
